/**
 * helpers.js
 * Formatting shared by the dashboard cards.
 *
 * The rule the whole screen follows: null means "not measured", and it is
 * never rendered as 0. A dashboard that shows a confident zero for something
 * nobody has counted is worse than one that says it doesn't know.
 *
 * Dependencies: components/Icons.jsx
 */

import { Facebook, Instagram } from '../Icons.jsx';

/** 1,284 · 12.9K · 1.2M — the compact form a stat tile wants. */
export function compact(value) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (Math.abs(n) >= 10_000) return `${trim(n / 1000)}K`;
  return n.toLocaleString();
}

/** 12.9 rather than 12.90, 1.2 rather than 1.20. */
const trim = (n) => Number(n.toFixed(1)).toString();

export const full = (value) =>
  value === null || value === undefined ? '—' : Number(value).toLocaleString();

export const pounds = (pence) => `£${((pence ?? 0) / 100).toFixed(2)}`;

export const percent = (value, places = 1) =>
  value === null || value === undefined ? '—' : `${Number(value).toFixed(places)}%`;

/**
 * The change between two figures, as a signed percentage.
 *
 * Returns null wherever a percentage would be a lie: with nothing to compare
 * against, "up 100%" and "up from nothing" are not the same statement, and
 * only the second one is true.
 */
export function change(current, previous) {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

/** Whether a movement should be shown in green or red. */
export const direction = (delta, upIsGood = true) => {
  if (delta === null || Math.abs(delta) < 0.05) return 'flat';
  return delta > 0 === upIsGood ? 'good' : 'bad';
};

// ============================================
// PLATFORMS
// ============================================

export const PLATFORMS = {
  facebook: { label: 'Facebook', Icon: Facebook, className: 'is-facebook' },
  instagram: { label: 'Instagram', Icon: Instagram, className: 'is-instagram' },
};

/** "facebook,instagram" → ['facebook', 'instagram'], de-duplicated. */
export const platformList = (value) =>
  [...new Set(String(value ?? '').split(',').map((p) => p.trim()).filter(Boolean))].filter(
    (p) => p in PLATFORMS,
  );

// ============================================
// TEXT
// ============================================

/** The opening words of a caption, for a one-line row. */
export function snippet(text, length = 52) {
  const clean = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > length ? `${clean.slice(0, length - 1)}…` : clean;
}

/** "2 hours ago" — how long ago something was touched. */
export function ago(iso) {
  if (!iso) return '';
  const then = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return `${Math.round(days / 30)} month${days < 45 ? '' : 's'} ago`;
}

/** Good morning / afternoon / evening, by the reader's own clock. */
export function greeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
